import { CommonModule } from '@angular/common';
import { Component, computed, effect, ElementRef, input, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AppInfo } from '../../../../../../../../global/environment';
import { FeatureFlags } from '../../../../../../../../global/featureFlags/featureFlags';
import { SmartFixSolution, Tracking } from '../../../../../../../../global/tracking/tracking';
import { ButtonComponent } from '../../../../../../../../shared/components/button/button.component';
import { DialogService } from '../../../../../../../../shared/components/dialog/services/dialog.service';
import { QMEncryptDirective } from '../../../../../../../../shared/directives/quantum-metric.directive';
import { OrbitFormClaimService } from '../../../../../services/orbit-form-claim.service';
import {
  ProcessedImage,
  SmartFixImageAttachComponent,
} from '../smart-fix-image-attach/smart-fix-image-attach-component.component';
import { SmartFixAnalyticsService } from '../utils/analytics/smart-fix-analytics-service';
import { LoadingDotsComponent } from '../utils/animations/chat-loading/chat-loading';
import { fadeInUp } from '../utils/animations/chatAnimations';
import { generateBedrockMessage, InputType, normaliseBrandName } from '../utils/helpers/bedrock-messages-helper';
import {
  AttachedImage,
  BedrockChatSession,
  BedrockMessages,
  SelectedImage,
} from '../utils/interfaces/bedrock-chat.interface';
import { NovaSonicVoiceService } from '../utils/nova-sonic-voice.service';
import { SmartFixImageAttachModal } from './smart-fix-image-attach-modal.component';
import { ModelResponseComponent } from './smart-fix-model-response/smart-fix-model-response';
import { SmartFixVoiceChatComponent } from './smart-fix-voice-chat/smart-fix-voice-chat.component';

@Component({
  selector: 'app-smart-fix-chat',
  imports: [
    LoadingDotsComponent,
    ModelResponseComponent,
    FormsModule,
    SmartFixImageAttachComponent,
    ButtonComponent,
    CommonModule,
    QMEncryptDirective,
    SmartFixVoiceChatComponent,
  ],
  templateUrl: './smart-fix-chat.component.html',
  styleUrls: ['./smart-fix-chat.component.scss', '../smart-fix.component.scss'],
  animations: [fadeInUp],
})
export class SmartFixChatComponent {
  novaSonicTesting = true; // set to true for now — hides legacy voice-capture toggle when Nova Sonic is active

  scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');
  chatInput = viewChild<ElementRef<HTMLTextAreaElement>>('chatInput');
  voiceChatComponent = viewChild(SmartFixVoiceChatComponent);
  imageAttachComponent = viewChild<SmartFixImageAttachComponent>('imageAttachComponent');
  chatLoading = input<boolean>();
  chatSession = input<BedrockChatSession>();
  sessionId = input<string>();
  solutionSelected = input<SmartFixSolution>();
  interactionMode = input<'Voice to Voice' | 'Text'>('Text');
  title = input<string>();
  showGreeting = input<boolean>();
  gatheringContextActive = input<boolean>();
  chatHistory = input<BedrockMessages[]>([]);
  showSafetyCheckInput = input<boolean>(false);
  agenticSolutionActiveFlag = input<boolean>(false);
  planNumber = input<string>('');
  bundleData = input<any>();

  addMessage = output<BedrockMessages>();
  contextGatheringDone = output<void>();
  sessionIdChanged = output<string>();
  firstInteractionStarted = output<void>();
  safetyCheckCompleted = output<void>();
  recordFireSafetyCheck = output<void>();
  interactionModeChanged = output<'Voice to Voice' | 'Text'>();
  goToRepairBooking = output<void>();

  chatMessage = signal({ content: '', type: 'text' });
  displayMessage = computed(() => this.chatMessage().content);
  interactionStarted: boolean = false;
  answers: { question: string; answer: string }[] = [];
  currentPrompt = '';
  showAttachImageTile = false;
  attachedImage: SelectedImage | null = null;
  summary = '';
  summaryWithImage = '';
  recognition: any | undefined = undefined;
  isListening: boolean = false;
  isScrollable: boolean = false;
  showTopGradient: boolean = false;
  showBottomGradient: boolean = false;
  private stickToBottom: boolean = true;
  showInputWrapperFocus: boolean = false;
  private isUsingKeyboard: boolean = false;

  // Safety check state
  showSafetyCheck: boolean = false;
  showFireQuestion: boolean = false;
  safetyCheckComplete: boolean = false;
  showEmergencyMessage: boolean = false;
  showSafetyInstructions: boolean = false;
  disableUserMessageDueToSafety: boolean = false;

  enableVoiceCapture: boolean = AppInfo.Environment?.smartFixFeatures?.enableVoiceCapture && this.isOnChrome;
  enableImageAttachment: boolean =
    AppInfo.Environment?.smartFixFeatures?.enableImageCapture && FeatureFlags.getFeatureFlag('smart-fix-image-attach');

  allowRetrievalOfAssets: boolean = AppInfo.Environment?.smartFixFeatures?.allowRetrievalOfAssets; // flag for allowing call to retrieve asset sent by user

  uploadSessionId: string = ''; // for retrieving assets

  claimBundleData = input<any>();
  V2VisLoading = input<boolean>(false);

  private textArea: HTMLTextAreaElement | null = null;
  private finalSpeechTranscript = '';
  private interimSpeechTranscript = '';

  constructor(
    private orbitFormClaimsService: OrbitFormClaimService,
    private smartFixAnalyticsService: SmartFixAnalyticsService,
    private dialog: DialogService,
    private route: ActivatedRoute,
    readonly novaSonicService: NovaSonicVoiceService
  ) {
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          this.isUsingKeyboard = true;
        }
      });
      document.addEventListener('mousedown', () => {
        this.isUsingKeyboard = false;
      });
    }

    effect(() => {
      this.showSafetyCheck = this.showSafetyCheckInput();

      this.textArea = this.chatInput()?.nativeElement ?? null;

      if (this.chatMessage().content.length > 0) {
        // so the 'Clear' button doesn't overlay the chat messages
        this.scrollToBottom();
      }

      const scrollContainer = this.scrollContainer()?.nativeElement;
      if (scrollContainer) {
        const onScroll = () => this.checkScrollable();
        scrollContainer.addEventListener('scroll', onScroll);

        return () => {
          scrollContainer.removeEventListener('scroll', onScroll);
        };
      }

      return () => {};
    });

    effect((onCleanup) => {
      const chatInput = this.chatInput()?.nativeElement;

      if (!chatInput) {
        return;
      }

      if (this.isNovaSonicSelected || this.isTextInputDisabled) {
        return;
      }

      const timeoutId = window.setTimeout(() => {
        this.focusChatInput();
      }, 0);

      onCleanup(() => clearTimeout(timeoutId));
    });

    // Scroll the voice transcript to the bottom whenever a new message
    // arrives (chatMessage() above only tracks the typed-text input box, so
    // voice-mode messages pushed in via chatHistory need their own trigger).
    // Only do this while the user is already at/near the bottom — otherwise a
    // message arriving while they've scrolled up to read earlier history
    // would yank them straight back down before they can read it.
    effect(() => {
      if (this.chatHistory().length > 0 && this.stickToBottom) {
        this.scrollToBottom();
      }
    });
  }

  protected viewModel = computed(() => {
    const bundle = this.claimBundleData()?.bundle || this.orbitFormClaimsService?.state?.claimData?.bundle;
    return {
      chatLoading: this.chatLoading(),
      chatHistory: this.chatHistory(),
      sessionId: this.sessionId(),
      gatheringContextActive: this.gatheringContextActive(),
      solutionSelected: this.solutionSelected(),
      showGreeting: this.showGreeting(),
      chatMessage: this.chatMessage(),
      title: this.title(),
      userName: bundle?.firstName,
      productType: bundle?.productType,
      brand: normaliseBrandName(bundle?.manufacturer),
      isHeatingPlan: bundle?.goodsColour === 'H',
    };
  });

  get vm() {
    return this.viewModel();
  }
  get isNovaSonicSelected() {
    return this.interactionMode() === 'Voice to Voice';
  }

  onSafetyCheckAnswer(hasBurningSmell: boolean): void {
    const answer = hasBurningSmell ? 'Yes' : 'No';
    const userMessage = generateBedrockMessage('user', this.vm.sessionId || '', { message: answer, type: 'text' });
    this.addMessage.emit(userMessage);

    Tracking.capture('smart_fix_safety_check_answer', {
      productType: this.vm.productType,
      isHeatingPlan: this.vm.isHeatingPlan,
      brand: this.vm.brand,
      hasBurningSmell,
      solution: this.vm.solutionSelected || 'unknown',
    });

    if (hasBurningSmell) {
      // If burning smell detected, continue to fire question
      this.showSafetyCheck = false;
      this.showFireQuestion = true;
      const agentMessage = generateBedrockMessage('agent', this.vm.sessionId || '', {
        message: 'Is the appliance on fire?',
        type: 'text',
      });
      this.addMessage.emit(agentMessage);
    } else {
      // If no burning smell, skip fire question and start context gathering
      this.showSafetyCheck = false;
      this.showFireQuestion = false;
      this.safetyCheckComplete = true;
      this.safetyCheckCompleted.emit();
    }
  }

  onFireCheckAnswer(isOnFire: boolean): void {
    const answer = isOnFire ? 'Yes' : 'No';
    const userMessage = generateBedrockMessage('user', this.vm.sessionId || '', { message: answer, type: 'text' });
    this.addMessage.emit(userMessage);
    const mmrLink = `${AppInfo.Environment}/my-plans/${this.planNumber()}/claims`;

    this.showFireQuestion = false;

    if (isOnFire) {
      this.showEmergencyMessage = true;
      const agentMessage = generateBedrockMessage('agent', this.vm.sessionId || '', {
        message: `Leave the property immediately and call 999.
        For further assistance, return back to the <a href="${mmrLink}">manage my repair page</a>. If you require further assistance when making a claim contact our customer service on 03330004966.
        `,
        type: 'text',
      });
      this.addMessage.emit(agentMessage);
      this.recordFireSafetyCheck.emit();

      this.safetyCheckComplete = true;
      this.disableUserMessageDueToSafety = true;
    } else {
      this.showSafetyInstructions = true;

      const safetyMessage = `If it's safe, switch off the machine and unplug it from the socket. If the plug socket is at risk of fire or electric shock, isolate the electrical supply to the appliance at the fuse box or consumer unit. To do this, switch off the mini circuit breaker (MCB) or residual current device (RCD) controlling the circuit. The circuit powering the appliance might have the label 'sockets'. Remove anything flammable away from the appliance. If smoke or fire continues, leave the property immediately and call 999.
        For further assistance, return back to the <a href="${mmrLink}">manage my repair page</a>. If you require further assistance when making a claim contact our customer service on 03330004966.
        `;
      const agentMessage = generateBedrockMessage('agent', this.vm.sessionId || '', {
        message: safetyMessage,
        type: 'text',
      });
      this.addMessage.emit(agentMessage);

      this.showSafetyCheck = false;
      this.showFireQuestion = false;
      this.showSafetyInstructions = false;
      this.safetyCheckComplete = true;
      this.disableUserMessageDueToSafety = true;
    }
  }

  completeSafetyCheck(): void {
    this.showSafetyCheck = false;
    this.showFireQuestion = false;
    this.showSafetyInstructions = false;
    this.safetyCheckComplete = true;

    // Emit to parent to start triage questions
    this.safetyCheckCompleted.emit();
  }

  handleTriageStep(): void {
    const userAnswer = this.chatMessage().content.trim();
    if (!userAnswer) return;

    const userMessage = generateBedrockMessage('user', this.vm.sessionId || '', { message: userAnswer, type: 'text' });
    this.addMessage.emit(userMessage);

    if (this.showAttachImageTile) {
      this.showAttachImageTile = false;
    }

    this.chatMessage.set({ content: '', type: 'text' });

    this.chatSession()
      ?.gatherContextFromUser({
        answers: this.answers,
        answer: userAnswer,
        productType: this.vm.productType,
        brand: this.vm.brand,
        imageAttachEnabled: this.enableImageAttachment,
        sessionId: this.vm.sessionId || '',
      })
      .then((res) => {
        this.answers = res.answers || [];

        if (!this.vm.sessionId) {
          const newSessionId = res.sessionId || 'unknown';
          this.sessionIdChanged.emit(newSessionId);
        }

        // store summary as soon as it's available
        if (res.summary) {
          this.summary = res.summary;
        }

        if (res.reAsk || res.nextQuestion) {
          this.currentPrompt = res.reAsk ?? res.nextQuestion;
          const agentMessage = generateBedrockMessage('agent', this.vm.sessionId || '', {
            message: this.currentPrompt,
            type: 'text',
          });
          this.addMessage.emit(agentMessage);

          if (res.showImageAttachmentPrompt) {
            this.showAttachImageTile = true;
            Tracking.capture('smart_fix_image_attach_viewed', {
              productType: this.vm.productType,
            });
          }

          if (res.nextQuestion?.includes('image')) {
            this.showAttachImageTile = true;
            Tracking.capture('smart_fix_image_attach_viewed', {
              productType: this.vm.productType,
            });
          }
          return;
        }

        if (res.done) {
          this.showAttachImageTile = false;
          this.contextGatheringDone.emit();
          this.summary = res.summary || this.summary;

          if (this.vm.solutionSelected === 'agent-llm' || this.agenticSolutionActiveFlag()) {
            this.startAgentConversationLLM(this.summary);
            return;
          }
          this.startBedrockChatRAG(this.summary); // pass summary to RAG lambda
          return;
        }
      });
    setTimeout(() => this.autoResize());
  }

  startBedrockChatRAG(summary: string) {
    this.chatSession()?.startSession(
      () => {
        this.firstInteractionStarted.emit();
      },
      'rag',
      summary,
      this.vm.sessionId
    );
  }

  startAgentConversationLLM(summary: string) {
    this.chatSession()
      ?.startAgentConversation(summary, this.vm.brand, this.sessionId() || '', this.vm.productType)
      .then((agentRes: any) => {
        const agentMessage = agentRes?.summary || agentRes?.message || agentRes?.response;
        const msg = generateBedrockMessage('agent', this.vm.sessionId || '', {
          message: agentMessage,
          type: 'text',
        });
        this.addMessage.emit(msg);
      })
      .catch((error) => {
        console.log('error', error);
      });
  }

  handleImageAttach(attachedImage: SelectedImage): void {
    this.showAttachImageTile = false;
    const image = attachedImage;

    const imageMessage: BedrockMessages = {
      role: 'user',
      sessionId: this.vm.sessionId || '',
      content: {
        message: '',
        type: 'image',
        timestamp: new Date().toISOString(),
        image: {
          data: image.preview.split(',')[1],
          mimeType: image.file.type,
          filename: image.file.name,
        },
      },
    };
    this.addMessage.emit(imageMessage);

    const userText = this.chatMessage()?.content?.trim();

    if (userText) {
      const textMessage = generateBedrockMessage('user', this.vm.sessionId || '', {
        message: userText,
        type: 'text',
      });
      this.addMessage.emit(textMessage);
    }

    this.chatMessage.set({ content: '', type: 'text' });
    setTimeout(() => this.autoResize());
    this.attachedImage = null;

    const payload = {
      image: { base64: image.preview.split(',')[1] },
      text: userText || '',
    };

    this.chatSession()
      ?.analyseImage(payload.text, payload.image, this.vm.productType)
      .then((res: any) => {
        if (res?.blocked === true) {
          this.attachedImage = null;
          this.showAttachImageTile = true;
          const blockedMsg = generateBedrockMessage('agent', this.vm.sessionId || '', {
            message: `Sorry, we can only process photos of appliances. Please upload one that doesn't show anything else.`,
            type: 'text',
          });
          this.addMessage.emit(blockedMsg);
          return;
        }

        // if approved, create uploadURL and save image in S3
        this.saveImage(attachedImage);
        this.contextGatheringDone.emit();

        const imageAnalysis = res?.content?.message || '';
        const userTextContext = userText ? ` User message: "${userText}".` : '';
        this.summaryWithImage = this.summary.concat(
          `${userTextContext}User also attached image with the following information summary: ${imageAnalysis}`
        );

        this.startAgentConversationLLM(this.summaryWithImage);
      })
      .catch((error) => {
        console.log('error', error);
      });
    return;
  }

  saveImage(image: SelectedImage): void {
    const trackingProps = {
      productType: this.vm.productType,
      isHeatingPlan: this.vm.isHeatingPlan,
      brand: this.vm.brand,
      solution: (this.vm.solutionSelected || 'unknown') as SmartFixSolution | 'unknown',
    };

    Tracking.capture('smart_fix_image_upload_attempt', trackingProps);

    this.chatSession()
      ?.createUploadUrlAndSave(image.file)
      .then((res) => {
        if (!res) {
          console.error('No response from createUploadUrlAndSave');
          Tracking.capture('smart_fix_image_upload_failure', { ...trackingProps, failureReason: 'no_response' });
          return;
        }

        const fileKey = res.fileKey;

        if (!fileKey) {
          Tracking.capture('smart_fix_image_upload_failure', { ...trackingProps, failureReason: 'no_file_key' });
          return;
        }

        // Save image meta data to Dynamo DB
        this.chatSession()
          ?.saveImageMetaData(
            res.fileKey,
            image.file.type,
            this.route.snapshot.params['planNumber'],
            this.vm.solutionSelected || '',
            this.vm.productType
          )
          .then(() => {
            Tracking.capture('smart_fix_image_upload_success', trackingProps);
            if (this.allowRetrievalOfAssets) {
              this.chatSession()?.getChatAssets();
            }
          })
          .catch(() => {
            Tracking.capture('smart_fix_image_upload_failure', {
              ...trackingProps,
              failureReason: 'metadata_save_failed',
            });
          });
      })
      .catch(() => {
        Tracking.capture('smart_fix_image_upload_failure', { ...trackingProps, failureReason: 'upload_url_failed' });
      });
  }

  // opens a modal to view the image
  onAttachedImageClick(image?: AttachedImage): void {
    const imageData = {
      data: this.attachedImage?.preview.split(',')[1] || image?.data,
      mimeType: this.attachedImage?.file.type || image?.mimeType,
      filename: this.attachedImage?.file.name || image?.filename,
    };

    this.dialog.openDialog(SmartFixImageAttachModal, {
      data: { sentImage: imageData },
      maxWidth: '720px',
      autoFocus: false,
    });
  }

  sendMessage(event: Event) {
    if (this.vm.chatLoading || !this.chatMessage().content.trim()) return;

    if (this.attachedImage) {
      this.handleImageAttach(this.attachedImage);
    }

    if (event instanceof KeyboardEvent && event.key !== 'Enter') {
      return;
    }

    if (this.isListening && this.recognition) {
      this.recognition.stop();
      this.isListening = false;
      this.skipNextResult = true;
    }

    const message = this.chatMessage().content.trim();
    const type = this.chatMessage().type;
    const userMessage = generateBedrockMessage('user', this.vm.sessionId || '', { message, type: type as InputType });

    // Track arrow-up icon click analytics
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      'clicked',
      '',
      'arrow-up icon',
      'smart-fix-virtual-assistant',
      'claims-sf-virtual-assistant'
    );

    this.addMessage.emit(userMessage);
    this.chatMessage.set({ content: '', type: 'text' });

    switch (this.vm.solutionSelected) {
      case 'rag':
        this.chatSession()?.sendMessage(message, this.vm.solutionSelected, type as InputType);
        break;
      case 'agent-llm':
        this.chatSession()?.continueAgentConversation(message, this.vm.productType, type as InputType);
        break;
      case 'chat-with-docs':
        this.chatSession()?.sendMessage(message, this.vm.solutionSelected, type as InputType);
        break;
      default:
        if (this.agenticSolutionActiveFlag()) {
          this.chatSession()?.continueAgentConversation(message, this.vm.productType, type as InputType);
        } else {
          this.chatSession()?.sendMessage(message, this.vm.solutionSelected ?? 'rag', type as InputType);
        }
        break;
    }
    setTimeout(() => this.autoResize());
  }

  onEnter(event: Event) {
    event.preventDefault();
    this.scrollToBottom();
    if (this.attachedImage) {
      this.handleImageAttach(this.attachedImage);
    }

    if (!this.vm.gatheringContextActive) {
      this.sendMessage(event);
      this.focusChatInput();
      return;
    } else {
      this.handleTriageStep();
      this.focusChatInput();
    }
  }

  onImageAttached(event: ProcessedImage): void {
    if (!event.success) {
      const blockedImg = generateBedrockMessage('agent', this.vm.sessionId || '', {
        message:
          event.error === 'too_large'
            ? 'Sorry, that file is too large. Please upload one under 5MB.'
            : 'Sorry, that isn’t an image file we can process. Please upload one with a format like JPEG, PNG or JPG.',
        type: 'text',
      });
      this.addMessage.emit(blockedImg);
      this.attachedImage = null;
      return;
    }
    this.attachedImage = event.image || null;
    setTimeout(() => this.autoResize());
  }

  amendImage(action: 'change' | 'remove', event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.attachedImage = null;
    setTimeout(() => this.autoResize());
    if (action === 'change') {
      this.imageAttachComponent()?.openFilePicker();
    }
  }

  onInputWrapperFocus(): void {
    this.showInputWrapperFocus = this.isUsingKeyboard;
  }

  onInputWrapperBlur(): void {
    this.showInputWrapperFocus = false;
  }

  autoResize(): void {
    const iconContainer = document.querySelector('.action-icon-container') as HTMLElement;
    iconContainer.style.transition = 'none';

    if (!this.textArea) return;

    if (!this.textArea?.value.trim()) {
      if (this.textArea) {
        this.resetTextAreaSize(this.textArea, iconContainer);
      }
      if (this.attachedImage) {
        iconContainer.classList.add('stacked');
      }
      return;
    }

    const scrollHeight = this.textArea.scrollHeight;
    const newHeight = Math.min(scrollHeight, 208);

    this.textArea.style.height = 'auto';

    this.textArea.style.height = `${newHeight}px`;
    this.textArea.style.overflowY = scrollHeight > newHeight ? 'scroll' : 'hidden';

    setTimeout(() => {
      this.updateIconStack(iconContainer, newHeight);
    }, 0);
  }

  voiceCaptureToggle(): void {
    if (!this.recognition) {
      this.initialiseSpeechRecognition();
    }

    Tracking.capture('smart_fix_start_voice_capture', {
      productType: this.vm.productType,
      isHeatingPlan: this.vm.isHeatingPlan,
      brand: this.vm.brand,
      solution: this.vm.solutionSelected || 'unknown',
    });

    if (this.isListening) {
      this.smartFixAnalyticsService.triggerSmartFixEvent(
        'clicked',
        '',
        'pause icon',
        'smart-fix-virtual-assistant',
        'claims-sf-virtual-assistant'
      );

      Tracking.capture('smart_fix_pause_voice_capture', {
        productType: this.vm.productType,
        isHeatingPlan: this.vm.isHeatingPlan,
        brand: this.vm.brand,
        solution: this.vm.solutionSelected || 'unknown',
      });
      this.recognition.stop();
      this.isListening = false;

      return;
    }

    // ensures new recording session doesn't keep the last transcript
    try {
      this.smartFixAnalyticsService.triggerSmartFixEvent(
        'clicked',
        '',
        'microphone icon',
        'smart-fix-virtual-assistant',
        'claims-sf-virtual-assistant'
      );
      this.skipNextResult = false;
      this.finalSpeechTranscript = this.textArea?.value || ''; // if user pauses, preserve existing message
      this.interimSpeechTranscript = '';
      this.recognition.start();
      this.isListening = true;
    } catch (error) {
      console.error('Error starting speech recognition:', error);
    }
  }

  initialiseSpeechRecognition(): void {
    const SpeechRecognition = (window as any).speechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      return;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.lang = 'en-US';
    this.recognition.interimResults = true;
    this.recognition.maxAlternatives = 1;
    this.recognition.continuous = true;

    this.finalSpeechTranscript = this.textArea?.value || this.chatMessage().content || '';

    this.recognition.onresult = (e: any) => {
      if (this.skipNextResult) {
        this.skipNextResult = false;
        return;
      }
      this.interimSpeechTranscript = '';

      for (let i = e.resultIndex, len = e.results.length; i < len; i++) {
        let transcript = e.results[i][0].transcript;

        // new speech is added to existing content without overwriting it
        if (e.results[i].isFinal) {
          this.finalSpeechTranscript += transcript;
        } else {
          this.interimSpeechTranscript += transcript;
        }
      }

      const combinedText = (this.finalSpeechTranscript + this.interimSpeechTranscript).trim();

      this.chatMessage.set({ content: combinedText, type: 'voice_to_text' });

      // need to expand text area as user is speaking
      setTimeout(() => {
        if (this.textArea) {
          this.textArea.scrollTop = this.textArea.scrollHeight;
          this.autoResize();
        }
      }, 0);
    };

    this.recognition.onend = () => {
      this.isListening = false;
    };
  }

  skipNextResult = false; // need this to allow a clear when listening is still active

  onClearButton(): void {
    this.skipNextResult = true; // ignore next result so old speech doesn't reppear
    if (this.textArea) {
      this.textArea.value = ''; // reset
    }

    this.chatMessage.set({ content: '', type: 'text' });
    this.finalSpeechTranscript = '';
    this.interimSpeechTranscript = '';
    this.autoResize();
  }

  onTypingProgress(): void {
    this.scrollToBottom();
  }

  // stack icons vertically  based on the text area height
  private updateIconStack(iconContainer: HTMLElement, newHeight: number): void {
    const isStacked = iconContainer.classList.contains('stacked');
    const shouldStack = this.enableVoiceCapture ? newHeight >= 80 : newHeight >= 40;

    if (!isStacked && shouldStack) {
      iconContainer.classList.add('stacked');
    } else if (isStacked && !shouldStack) {
      iconContainer.classList.remove('stacked');
    }
  }

  private resetTextAreaSize(textArea: HTMLTextAreaElement, iconContainer: HTMLElement): void {
    if (!textArea) return;
    textArea.style.height = '22px';
    textArea.style.overflowY = 'hidden';
    iconContainer.classList.remove('stacked');
  }

  private checkScrollable(): void {
    const containerRef = this.scrollContainer()?.nativeElement;
    if (!containerRef) return;

    this.isScrollable = containerRef.scrollHeight > containerRef.clientHeight;
    this.showTopGradient = this.isScrollable && containerRef.scrollTop > 0; // top gradient on chat messages

    if (containerRef.scrollTop === 0) {
      // if at top of container, set to false
      this.showTopGradient = false;
    }

    // Only auto-scroll on new messages while the user is already near the
    // bottom. Otherwise a message arriving while they've scrolled up to read
    // earlier history yanks them straight back down before they can read it.
    // The voice transcript grows with the page rather than scrolling
    // internally, so "near the bottom" is measured against the window's
    // scroll position when the container itself has no overflow of its own.
    const distanceFromBottom = this.isScrollable
      ? containerRef.scrollHeight - containerRef.scrollTop - containerRef.clientHeight
      : document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
    this.stickToBottom = distanceFromBottom < 48;
    // bottom fade only when there's more content below to hint at — never
    // dims the newest message, which is always fully visible at the bottom
    this.showBottomGradient = this.isScrollable && distanceFromBottom >= 48;
  }

  private scrollToBottom(): void {
    const containerRef = this.scrollContainer()?.nativeElement;
    if (!containerRef) return;

    this.checkScrollable();

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.isScrollable) {
          containerRef.scrollTo({
            top: containerRef.scrollHeight,
            behavior: 'smooth',
          });
        } else {
          // No internal overflow — the container grows with the page, so
          // scroll the page itself to bring the latest message into view.
          containerRef.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 0);
    });
  }

  private focusChatInput(): void {
    this.textArea = this.chatInput()?.nativeElement ?? null;

    if (!this.textArea || this.isNovaSonicSelected || this.isTextInputDisabled) {
      return;
    }

    this.textArea.focus();
  }

  // speech recognition api only supported on chrome so use this to hide mic icon
  get isOnChrome() {
    return /chrom(e|ium)/.test(navigator.userAgent.toLowerCase());
  }

  get formattedUserName() {
    return this.vm.userName.charAt(0).toUpperCase() + this.vm.userName.substring(1).toLowerCase();
  }

  get shouldShowVoiceCaptureToggle() {
    return (
      this.enableVoiceCapture &&
      !this.disableUserMessageDueToSafety &&
      !this.showSafetyCheck &&
      !this.showFireQuestion &&
      !this.novaSonicTesting
    );
  }

  get isTextInputDisabled() {
    return this.isListening || this.showSafetyCheck || this.showFireQuestion || this.disableUserMessageDueToSafety;
  }
}
